import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type { DocumentWithBlocks, UpdateDocumentInput } from "@/lib/api";

export function useDocument(id: string) {
  const { fetchWithAuth } = useApiContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["document", id],
    queryFn: async () => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${id}`);
      const document = await handleResponse<DocumentWithBlocks>(response);
      return document;
    },
    enabled: !!id,
  });

  return {
    document: data,
    isLoading,
    error,
    refetch,
  };
}

export function useUpdateDocument(id: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (data: UpdateDocumentInput) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      return handleResponse<DocumentWithBlocks>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document", id] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}
